import { createRouter } from '@tanstack/react-router';
import { PageLoader } from '@opengoal/ui';
import { queryClient } from '@/app/queryClient';
import { appLayoutRoute, authLayoutRoute, rootRoute } from '@/app/routes/roots';
import { agenciesRoutes } from '@/modules/agencies';
import { authRoutes } from '@/modules/auth';
import { dashboardRoutes } from '@/modules/dashboard';
import { salesRequestsRoutes } from '@/modules/sales-requests';
import { settingsRoutes } from '@/modules/settings';
import { tiersRoutes } from '@/modules/tiers';
import { usersRoutes } from '@/modules/users';

/**
 * The route tree.
 *
 * This is the ONLY file that knows about every module, and all it does is
 * assemble their exported route lists. Modules define their own routes next to
 * their own pages, so adding a feature never means editing a central table of
 * paths that everyone conflicts on.
 */
const routeTree = rootRoute.addChildren([
  authLayoutRoute.addChildren(authRoutes),
  appLayoutRoute.addChildren([
    ...dashboardRoutes,
    ...agenciesRoutes,
    ...tiersRoutes,
    ...salesRequestsRoutes,
    ...usersRoutes,
    ...settingsRoutes,
  ]),
]);

export const router = createRouter({
  routeTree,
  context: { queryClient },
  // Start loading a route's chunk when the user hovers its link. Costs nothing
  // when they don't click and removes the visible delay when they do.
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  defaultPendingComponent: PageLoader,
  scrollRestoration: true,
});

// Registers the router's types globally, which is what makes `useSearch`,
// `useParams` and `navigate({ to: ... })` type-checked against the real tree
// rather than accepting any string.
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
